import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { Button } from '../ui/button';
import { AsyncButton } from '../ui/async-button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { ThemeToggle } from '../ui/theme-toggle';
import {
  User,
  Send,
  Loader2,
  Bell,
  Trash2,
  Menu,
  HelpCircle,
  MoreVertical,
  Moon,
  Sun,
} from 'lucide-react';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';
import { cn } from '../../lib/utils';
import { useResponsive } from '../../context/ResponsiveContext';
import {
  hasAdminLikeAccess,
  getUserRoleLabel,
  formatLabelWithLocation,
} from '../../utils/userAccess';
import MySkillsHeaderDiscovery from './MySkillsHeaderDiscovery';
import MonthSelect from './MonthSelect';
import BrandLogo from './BrandLogo';
import { getDefaultDashboardPath, resolvePrimaryRole } from '../../utils/rolePriority';

/** 44px touch hit area through tablet tier; compact 32px from laptop (lg) up. */
const MOBILE_HEADER_ICON =
  'h-11 w-11 min-h-[44px] min-w-[44px] lg:h-8 lg:w-8 lg:min-h-8 lg:min-w-8';

function getUserInitials(name) {
  const trimmed = (name || '').trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

function TotalHoursBadge({ hours, compact = false, walkthroughId }) {
  return (
    <div
      className="flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800 whitespace-nowrap flex-shrink-0 md:gap-1.5 md:px-2 md:py-1"
      aria-label={`Total hours: ${hours}`}
      data-walkthrough={walkthroughId}
    >
      <span className="text-ds-caption text-blue-700 dark:text-blue-300 font-medium">
        {compact ? 'Hours:' : 'Total Hours:'}
      </span>
      <span className="text-ds-h3 font-semibold text-blue-900 dark:text-blue-200">{hours}h</span>
    </div>
  );
}

function SubmitButton({
  onClick,
  disabled,
  isSubmitting,
  fullLabel = false,
  iconOnly = false,
  className,
  walkthroughId,
}) {
  return (
    <Button
      onClick={onClick}
      className={className}
      disabled={disabled}
      size={iconOnly ? 'icon' : 'sm'}
      aria-label="Submit for Approval"
      data-testid="submit-timesheet"
      data-walkthrough={walkthroughId}
    >
      {isSubmitting ? (
        <>
          <Loader2 className={cn('flex-shrink-0 animate-spin', iconOnly ? 'h-4 w-4' : 'h-3.5 w-3.5 md:h-4 md:w-4 mr-1 md:mr-1.5')} />
          {!iconOnly && (fullLabel ? <span>Submitting...</span> : <span>Submitting...</span>)}
        </>
      ) : (
        <>
          <Send className={cn('flex-shrink-0', iconOnly ? 'h-4 w-4' : 'h-3.5 w-3.5 md:h-4 md:w-4', !iconOnly && !fullLabel && 'mr-1 md:mr-1.5')} />
          {iconOnly ? null : fullLabel ? (
            <span>Submit for Approval</span>
          ) : (
            <span>Submit</span>
          )}
        </>
      )}
    </Button>
  );
}

function NotificationBell({
  notifications,
  notificationOpen,
  onNotificationOpenChange,
  onDeleteNotification,
  deletingNotificationId = null,
  hideTrigger = false,
}) {
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <Popover open={notificationOpen} onOpenChange={onNotificationOpenChange} modal={false}>
      {hideTrigger ? (
        <PopoverTrigger asChild>
          <button
            type="button"
            className="pointer-events-none absolute h-0 w-0 overflow-hidden border-0 p-0 opacity-0"
            tabIndex={-1}
            aria-hidden="true"
          />
        </PopoverTrigger>
      ) : (
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className={cn(
              MOBILE_HEADER_ICON,
              'relative flex items-center justify-center flex-shrink-0 p-0 dark:border-gray-700 dark:hover:bg-gray-800'
            )}
            aria-label={
              unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'
            }
          >
            <Bell className="h-4 w-4 text-gray-600 dark:text-gray-300" aria-hidden="true" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-600 px-0.5 text-ds-caption font-semibold text-white border border-white dark:border-gray-800">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Button>
        </PopoverTrigger>
      )}
      <PopoverContent
        className="w-popover-mobile sm:w-80 md:w-96 p-0 dark:bg-gray-900 dark:border-gray-700"
        align="end"
        sideOffset={15}
      >
        <div className="flex items-center justify-between p-3 border-b dark:border-gray-700">
          <h3 className="text-ds-h2 text-gray-900 dark:text-gray-100">Notifications</h3>
        </div>
        <div className="max-h-[min(60dvh,400px)] overflow-y-auto" aria-live="polite">
          {notifications.length === 0 ? (
            <div className="p-3 text-center text-gray-500 dark:text-gray-400">
              <Bell className="h-8 w-8 mx-auto mb-2 text-gray-400 dark:text-gray-500" />
              <p className="text-ds-body">No notifications</p>
            </div>
          ) : (
            <div className="divide-y dark:divide-gray-700">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors min-h-[44px] ${
                    !notification.isRead ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                  }`}
                  aria-label={
                    !notification.isRead
                      ? `Unread notification: ${notification.title}`
                      : undefined
                  }
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-ds-body font-medium text-gray-900 dark:text-gray-100 mb-1">
                        {notification.title}
                      </p>
                      <p className="text-ds-body text-gray-600 dark:text-gray-300">
                        {notification.message}
                      </p>
                      <p className="text-ds-caption text-gray-400 dark:text-gray-500 mt-2">
                        {new Date(notification.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                    <AsyncButton
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 min-h-[44px] min-w-[44px] -m-1.5 flex-shrink-0 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/20"
                      onClick={(e) => onDeleteNotification(notification.id, e)}
                      pending={deletingNotificationId === notification.id}
                      loadingText="Deleting…"
                      aria-label="Delete notification"
                    >
                      <Trash2 className="h-4 w-4" />
                    </AsyncButton>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function HeaderOverflowMenu({
  notifications,
  onNotificationOpenChange,
  isAdmin,
  showAdminRequestBell,
  pendingRequestsCount,
  onNavigateAdminRequests,
  onAppTour,
  isWalkthroughEnabled,
  showNotificationsMenuItem = true,
  showAdminRequestMenuItem = true,
}) {
  const { theme, setTheme } = useTheme();
  const unreadCount = notifications.filter((n) => !n.isRead).length;
  const isDark = theme === 'dark';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className={cn(MOBILE_HEADER_ICON, 'shrink-0 dark:border-gray-700 dark:hover:bg-gray-800')}
          aria-label="More actions"
          data-testid="header-overflow-trigger"
        >
          <MoreVertical className="h-4 w-4 text-gray-600 dark:text-gray-300" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 dark:bg-gray-900 dark:border-gray-700">
        {!isAdmin && showNotificationsMenuItem && (
          <DropdownMenuItem
            className="min-h-[44px] cursor-pointer dark:focus:bg-gray-800"
            onSelect={() => onNotificationOpenChange(true)}
          >
            <Bell className="h-4 w-4 text-gray-600 dark:text-gray-300" aria-hidden="true" />
            <span className="flex-1">Notifications</span>
            {unreadCount > 0 && (
              <span className="rounded-full bg-red-600 px-1.5 py-0.5 text-ds-caption font-semibold text-white">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </DropdownMenuItem>
        )}
        {showAdminRequestBell && showAdminRequestMenuItem && (
          <DropdownMenuItem
            className="min-h-[44px] cursor-pointer dark:focus:bg-gray-800"
            onSelect={onNavigateAdminRequests}
          >
            <Bell className="h-4 w-4 text-gray-600 dark:text-gray-300" aria-hidden="true" />
            <span className="flex-1">Project Access Requests</span>
            {pendingRequestsCount > 0 && (
              <span className="rounded-full bg-red-600 px-1.5 py-0.5 text-ds-caption font-semibold text-white">
                {pendingRequestsCount > 99 ? '99+' : pendingRequestsCount}
              </span>
            )}
          </DropdownMenuItem>
        )}
        {isWalkthroughEnabled && onAppTour && (
          <DropdownMenuItem
            className="min-h-[44px] cursor-pointer dark:focus:bg-gray-800"
            onSelect={onAppTour}
          >
            <HelpCircle className="h-4 w-4 text-gray-600 dark:text-gray-300" aria-hidden="true" />
            App tour
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="dark:bg-gray-700" />
        <DropdownMenuItem
          className="min-h-[44px] cursor-pointer dark:focus:bg-gray-800"
          onSelect={() => setTheme(isDark ? 'light' : 'dark')}
        >
          {isDark ? (
            <Sun className="h-4 w-4 text-gray-600 dark:text-gray-300" aria-hidden="true" />
          ) : (
            <Moon className="h-4 w-4 text-gray-600 dark:text-gray-300" aria-hidden="true" />
          )}
          {isDark ? 'Light mode' : 'Dark mode'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserProfileCluster({ user, rmMenuAccess, isTablet, isDesktop }) {
  const initials = getUserInitials(user?.name);
  const roleLabel = formatLabelWithLocation(getUserRoleLabel(user, { rmMenuAccess }), user?.workingLocation);

  return (
    <TooltipProvider>
      <Link
        to="/account/profile"
        className="flex min-w-0 shrink-0 items-center gap-1 border-l border-gray-200 pl-1 dark:border-gray-700 md:gap-1.5 md:pl-1.5 lg:pl-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        data-walkthrough="theme-profile"
        aria-label="Open my profile"
      >
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30 lg:h-8 lg:w-8"
          aria-hidden={isDesktop}
        >
          {isTablet ? (
            <span className="text-ds-caption font-semibold text-blue-700 dark:text-blue-300">{initials}</span>
          ) : (
            <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          )}
        </div>
        {isDesktop && (
          <div className="min-w-0 max-w-[140px] xl:max-w-[160px]">
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="truncate text-ds-body font-medium leading-tight text-gray-900 dark:text-gray-100">
                  {user.name || 'Loading...'}
                </p>
              </TooltipTrigger>
              <TooltipContent className="dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
                <p>{user.name || 'Loading...'}</p>
              </TooltipContent>
            </Tooltip>
            <p className="truncate text-ds-caption leading-tight text-gray-500 dark:text-gray-400">{roleLabel}</p>
          </div>
        )}
        {isTablet && (
          <span className="sr-only">
            {user.name || 'Loading...'}, {roleLabel}
          </span>
        )}
      </Link>
    </TooltipProvider>
  );
}

export default function LayoutHeader({
  user,
  rmMenuAccess,
  sidebar,
  layout = 'full',
  showMonthSelector,
  showHoursDisplay,
  showSubmitButton,
  selectedMonth,
  onMonthChange,
  availableMonths,
  totalMonthHours,
  timesheetStatus,
  canSubmit,
  isSubmitting,
  isSaving,
  onSubmit,
  notifications,
  notificationOpen,
  onNotificationOpenChange,
  onDeleteNotification,
  deletingNotificationId = null,
  pendingRequestsCount,
  onAppTour,
}) {
  const navigate = useNavigate();
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const { isWalkthroughEnabled } = useFeatureFlags();

  const isAdmin = hasAdminLikeAccess(user);
  const showDraftSubmit = showSubmitButton && timesheetStatus === 'draft';
  const showAdminRequestBell = isAdmin && pendingRequestsCount !== null;
  const isInset = layout === 'inset';
  const collapseSecondaryIntoOverflow = isMobile;
  const homePath = useMemo(
    () => getDefaultDashboardPath(resolvePrimaryRole(user)),
    [user]
  );

  return (
    <header
      className={cn(
        'bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-700 z-[100] flex-shrink-0',
        isInset
          ? 'relative shrink-0 safe-area-right'
          : 'fixed top-[var(--browser-recommendation-banner-h,0px)] left-0 right-0 header-safe safe-area-left safe-area-right'
      )}
    >
      <div className="flex h-[var(--header-h)] w-full min-w-0 items-center justify-between gap-0.5 overflow-hidden px-1.5 max-md:gap-0 max-md:px-1.5 sm:px-2 md:gap-2 md:px-4 lg:gap-2 lg:px-4">
        {/* Left cluster — flex-1 + overflow-hidden so brand truncates before overlapping right cluster */}
        <div
          data-testid="header-left-cluster"
          className="flex min-w-0 flex-1 items-center gap-0.5 overflow-hidden max-md:gap-0 md:gap-2 lg:flex-none lg:overflow-visible"
        >
          {!isInset && isMobile && (
            <Button
              variant="ghost"
              size="icon"
              className={cn(MOBILE_HEADER_ICON, 'shrink-0')}
              data-walkthrough="nav-mobile-menu"
              onClick={sidebar.openMobileDrawer}
              aria-label="Open navigation"
            >
              <Menu className="h-4 w-4 md:h-5 md:w-5" />
            </Button>
          )}

          {!isInset ? (
            <Link
              to={homePath}
              data-testid="header-brand"
              className="flex min-w-0 max-w-[2rem] shrink items-center overflow-hidden rounded-lg px-0 outline-none transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset sm:max-w-[2.25rem] md:max-w-[min(9rem,calc(100vw-22rem))] lg:max-w-none lg:shrink-0 dark:hover:bg-gray-800/60"
              aria-label="KadelLabs home"
            >
              <BrandLogo variant={isMobile ? 'mark' : 'full'} className="min-w-0" />
            </Link>
          ) : null}

          {!isMobile && showMonthSelector && (
            <div className="shrink-0" data-walkthrough="timesheet-month-nav-desktop">
              <MonthSelect
                id="header-month-select"
                value={selectedMonth}
                onValueChange={onMonthChange}
                months={availableMonths}
                touchFriendly={isTablet}
              />
            </div>
          )}
        </div>

        {/* Right cluster — timesheet chrome in header from tablet up; secondary icons overflow on mobile */}
        <div
          data-testid="header-right-cluster"
          className="flex shrink-0 items-center justify-end gap-0 overflow-hidden max-md:gap-0 sm:gap-0.5 md:gap-1.5 lg:gap-2"
        >
          <MySkillsHeaderDiscovery user={user} />

          {!isMobile && showHoursDisplay && (
            <TotalHoursBadge
              hours={totalMonthHours}
              compact={isTablet}
              walkthroughId="timesheet-hours-summary-desktop"
            />
          )}

          {!isMobile && showDraftSubmit && (
            <SubmitButton
              onClick={onSubmit}
              disabled={!canSubmit || isSubmitting || isSaving}
              isSubmitting={isSubmitting}
              fullLabel={isDesktop}
              walkthroughId="timesheet-submit-desktop"
              className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 whitespace-nowrap flex-shrink-0 min-h-[44px] h-11 px-2 font-medium lg:h-8 lg:min-h-8 lg:px-2.5"
            />
          )}

          {!isMobile && (
            <UserProfileCluster
              user={user}
              rmMenuAccess={rmMenuAccess}
              isTablet={isTablet}
              isDesktop={isDesktop}
            />
          )}

          {collapseSecondaryIntoOverflow ? (
            <>
              {!isAdmin && (
                <NotificationBell
                  notifications={notifications}
                  notificationOpen={notificationOpen}
                  onNotificationOpenChange={onNotificationOpenChange}
                  onDeleteNotification={onDeleteNotification}
                  deletingNotificationId={deletingNotificationId}
                />
              )}
              {showAdminRequestBell && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => navigate('/admin/notifications')}
                  className={cn(
                    MOBILE_HEADER_ICON,
                    'relative flex items-center justify-center flex-shrink-0 p-0 dark:border-gray-700 dark:hover:bg-gray-800'
                  )}
                  aria-label={`Project Access Requests${pendingRequestsCount > 0 ? ` (${pendingRequestsCount} pending)` : ''}`}
                >
                  <Bell className="h-4 w-4 text-gray-600 dark:text-gray-300" aria-hidden="true" />
                  {pendingRequestsCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-600 text-white text-ds-caption font-semibold flex items-center justify-center border-2 border-white dark:border-gray-800">
                      {pendingRequestsCount > 99 ? '99+' : pendingRequestsCount}
                    </span>
                  )}
                </Button>
              )}
              <HeaderOverflowMenu
                notifications={notifications}
                onNotificationOpenChange={onNotificationOpenChange}
                isAdmin={isAdmin}
                showAdminRequestBell={showAdminRequestBell}
                pendingRequestsCount={pendingRequestsCount}
                onNavigateAdminRequests={() => navigate('/admin/notifications')}
                onAppTour={onAppTour}
                isWalkthroughEnabled={isWalkthroughEnabled}
                showNotificationsMenuItem={isAdmin}
                showAdminRequestMenuItem={false}
              />
            </>
          ) : (
            <>
              {!isAdmin && (
                <NotificationBell
                  notifications={notifications}
                  notificationOpen={notificationOpen}
                  onNotificationOpenChange={onNotificationOpenChange}
                  onDeleteNotification={onDeleteNotification}
                  deletingNotificationId={deletingNotificationId}
                />
              )}

              {showAdminRequestBell && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => navigate('/admin/notifications')}
                  className={cn(
                    MOBILE_HEADER_ICON,
                    'relative flex items-center justify-center flex-shrink-0 p-0 dark:border-gray-700 dark:hover:bg-gray-800'
                  )}
                  aria-label={`Project Access Requests${pendingRequestsCount > 0 ? ` (${pendingRequestsCount} pending)` : ''}`}
                >
                  <Bell className="h-4 w-4 text-gray-600 dark:text-gray-300" aria-hidden="true" />
                  {pendingRequestsCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-600 text-white text-ds-caption font-semibold flex items-center justify-center border-2 border-white dark:border-gray-800">
                      {pendingRequestsCount > 99 ? '99+' : pendingRequestsCount}
                    </span>
                  )}
                </Button>
              )}

              {isWalkthroughEnabled && onAppTour ? (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className={cn(MOBILE_HEADER_ICON, 'shrink-0 dark:border-gray-700 dark:hover:bg-gray-800')}
                  onClick={onAppTour}
                  aria-label="Start app tour"
                  data-walkthrough="header-app-tour"
                >
                  <HelpCircle className="h-4 w-4 text-gray-600 dark:text-gray-300" aria-hidden="true" />
                </Button>
              ) : null}

              <ThemeToggle />
            </>
          )}
        </div>
      </div>
    </header>
  );
}
